import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import Label from "../../../../../../components/form_fields/Label";
import OrangeButton from "../../../../../../components/buttons/OrangeButton";
import { useUpdateCustomTaxRelief } from "../../../../../../services/custom-tax-relief.service";
import { CustomTaxReliefDto } from "../../../../../../models/custom-tax-relief.dto";

interface IFormInputs {
	customTaxReliefId: number;
}

interface IAddTaxReliefForm {
	employeeId: number;
	availableReliefs: CustomTaxReliefDto[];
	setShowAddForm: React.Dispatch<React.SetStateAction<boolean>>;
	onSuccess: () => void;
}

const AddTaxReliefForm = ({
	employeeId,
	availableReliefs,
	setShowAddForm,
	onSuccess,
}: IAddTaxReliefForm) => {
	const { mutate: updateCustomTaxRelief } = useUpdateCustomTaxRelief();
	const [uploading, setUploading] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<IFormInputs>();

	const onSubmit: SubmitHandler<IFormInputs> = (data) => {
		setUploading(true);

		updateCustomTaxRelief(
			{
				id: Number(data.customTaxReliefId),
				payload: {
					addEmployeeIds: [employeeId],
				},
			},
			{
				onSuccess: () => {
					onSuccess();
					setShowAddForm(false);
				},
				onSettled: () => setUploading(false),
			},
		);
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className="lg:grid grid-cols-2 gap-x-4 gap-y-6 mt-6 space-y-4 lg:space-y-0">
				<div className="w-full">
					<Label title="Custom Tax Relief" for="customTaxReliefId" />
					<select
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
							errors.customTaxReliefId
								? "border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
						{...register("customTaxReliefId", {
							required: "Select a custom tax relief here",
						})}
					>
						<option value="">Select Custom Tax Relief</option>
						{availableReliefs.map((relief) => (
							<option key={relief.id} value={relief.id}>
								{relief.name} ({relief.code})
							</option>
						))}
					</select>
					{errors.customTaxReliefId && (
						<p className="text-red-500 text-sm mt-1">
							{errors.customTaxReliefId.message}
						</p>
					)}
					{availableReliefs.length === 0 && (
						<p className="text-gray-400 text-xs mt-2">
							All active tax reliefs have already been assigned to this
							employee.
						</p>
					)}
				</div>
			</div>

			<div className="w-full flex items-center gap-4 lg:justify-between my-8">
				<button
					type="button"
					onClick={() => setShowAddForm(false)}
					className="px-10 py-4 max-lg:w-full border text-gray-400 hover:text-gray-500 border-gray-300 rounded-lg text-2xs"
				>
					Close Form
				</button>
				<OrangeButton
					title={uploading ? "Saving..." : "Assign Relief"}
					type="submit"
					className="h-14 px-8 max-lg:w-full"
					disabled={uploading || availableReliefs.length === 0}
				/>
			</div>
		</form>
	);
};

export default AddTaxReliefForm;
